"use client"

import { Head, useForm, usePage } from "@inertiajs/react"
import type React from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { SectionFooter } from "@/components/ui/section-footer"
import { TextArea } from "@/components/ui/textarea"
import { DashboardLayout } from "@/layouts/dashboard-layout"
import { adminCollabsPath } from "@/routes"

interface NewCollabForm {
  brief: {
    title: string
    subtitle: string
    main_headline: string
    strategic_context: string
    main_body: string
    summary: string
  }
}

const defaultValues: NewCollabForm = {
  brief: {
    title: "",
    subtitle: "",
    main_headline: "",
    strategic_context: "",
    main_body: "",
    summary: "",
  },
}

/**
 * CollabsNew is a page component for creating a new collab.
 */
export default function CollabsNew() {
  const { errors } = usePage<{ errors?: Record<string, string[]> }>().props
  const { data, setData, post, processing } =
    useForm<NewCollabForm>(defaultValues)

  const titleError = errors?.title?.[0]
  const subtitleError = errors?.subtitle?.[0]
  const mainHeadlineError = errors?.main_headline?.[0]
  const strategicContextError = errors?.strategic_context?.[0]
  const mainBodyError = errors?.main_body?.[0]
  const summaryError = errors?.summary?.[0]

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    post(adminCollabsPath())
  }

  return (
    <DashboardLayout>
      <Head title="New Collab" />
      <main className="mx-auto flex w-full max-w-2xl flex-col gap-8 px-4 pt-8 pb-12 lg:px-8">
        <header className="flex flex-col gap-0.5">
          <h1 className="text-primary font-display md:text-display-xs text-xl font-semibold">
            New collab
          </h1>
          <p className="text-md text-tertiary">
            Open a call for community playlist suggestions. Title is required —
            the rest can be filled in later.
          </p>
        </header>

        <form onSubmit={submit} className="flex flex-col gap-6">
          <Input
            isRequired
            label="Title"
            placeholder="Songs for the late shift"
            value={data.brief.title}
            onChange={(value) =>
              setData("brief", { ...data.brief, title: value })
            }
            isInvalid={!!titleError}
            errorMessage={titleError}
          />

          <Input
            label="Subtitle"
            placeholder="A short line under the title. (optional)"
            value={data.brief.subtitle}
            onChange={(value) =>
              setData("brief", { ...data.brief, subtitle: value })
            }
            isInvalid={!!subtitleError}
            errorMessage={subtitleError}
          />

          <Input
            label="Main headline"
            placeholder="The headline shown in the body. (optional)"
            value={data.brief.main_headline}
            onChange={(value) =>
              setData("brief", { ...data.brief, main_headline: value })
            }
            isInvalid={!!mainHeadlineError}
            errorMessage={mainHeadlineError}
          />

          <TextArea
            label="Strategic context"
            hint={strategicContextError}
            isInvalid={!!strategicContextError}
            placeholder="Why this collab exists right now. (optional)"
            value={data.brief.strategic_context}
            onChange={(value) =>
              setData("brief", { ...data.brief, strategic_context: value })
            }
            rows={3}
          />

          <TextArea
            label="Main body"
            hint={mainBodyError ?? "Supports basic markdown."}
            isInvalid={!!mainBodyError}
            placeholder="The main body copy for this collab. (optional)"
            value={data.brief.main_body}
            onChange={(value) =>
              setData("brief", { ...data.brief, main_body: value })
            }
            rows={6}
          />

          <TextArea
            label="Summary"
            hint={summaryError}
            isInvalid={!!summaryError}
            placeholder="A line on what this collab is about. (optional)"
            value={data.brief.summary}
            onChange={(value) =>
              setData("brief", { ...data.brief, summary: value })
            }
            rows={4}
          />

          <SectionFooter.Root>
            <SectionFooter.Actions>
              <Button
                type="button"
                color="secondary"
                size="md"
                onClick={() => window.history.back()}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                size="md"
                isDisabled={processing}
              >
                Create
              </Button>
            </SectionFooter.Actions>
          </SectionFooter.Root>
        </form>
      </main>
    </DashboardLayout>
  )
}
